import { supabase } from './supabaseClient';

/**
 * Device Alert Rule Service
 * Manages fine-grained alert rules for individual devices or groups of devices
 */

export const deviceAlertRuleService = {
  /**
   * Get all alert rules for the current user
   */
  async getAlertRules(userId) {
    try {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Fetch rules with their associated sensors
      const { data: rules, error: rulesError } = await supabase
        .from('device_alert_rules')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (rulesError) throw rulesError;
      if (!rules || rules.length === 0) return [];

      // Fetch sensor associations for all rules
      const ruleIds = rules.map(r => r.id);
      const { data: ruleSensors, error: sensorsError } = await supabase
        .from('device_alert_rule_sensors')
        .select('rule_id, sensor_id')
        .in('rule_id', ruleIds);

      if (sensorsError) throw sensorsError;

      // Map sensors to rules
      const sensorMap = new Map();
      ruleSensors?.forEach(rs => {
        if (!sensorMap.has(rs.rule_id)) {
          sensorMap.set(rs.rule_id, []);
        }
        sensorMap.get(rs.rule_id).push(rs.sensor_id);
      });

      // Transform to DeviceAlertRule format
      return rules.map(rule => ({
        id: rule.id,
        userId: rule.user_id,
        ruleName: rule.rule_name,
        description: rule.description,
        alertType: rule.alert_type, // 'push' | 'sms' | 'both'
        tempMinEnabled: rule.temp_min_enabled || false,
        tempMinValue: rule.temp_min_value,
        tempMaxEnabled: rule.temp_max_enabled || false,
        tempMaxValue: rule.temp_max_value,
        humidityMinEnabled: rule.humidity_min_enabled || false,
        humidityMinValue: rule.humidity_min_value,
        humidityMaxEnabled: rule.humidity_max_enabled || false,
        humidityMaxValue: rule.humidity_max_value,
        batteryLowEnabled: rule.battery_low_enabled || false,
        batteryLowValue: rule.battery_low_value,
        smsPhoneNumber: rule.sms_phone_number,
        cooldownMinutes: rule.cooldown_minutes || 30,
        timeWindowEnabled: rule.time_window_enabled || false,
        timeWindowStart: rule.time_window_start,
        timeWindowEnd: rule.time_window_end,
        timeWindowDays: rule.time_window_days,
        isActive: rule.is_active !== undefined ? rule.is_active : true,
        sensorIds: sensorMap.get(rule.id) || [],
        createdAt: rule.created_at,
        updatedAt: rule.updated_at,
      }));
    } catch (error) {
      console.error('Failed to fetch alert rules:', error);
      throw error;
    }
  },

  /**
   * Create a new alert rule
   */
  async createAlertRule(userId, input) {
    try {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Validate input
      if (!input.ruleName || !input.alertType || !input.sensorIds || input.sensorIds.length === 0) {
        throw new Error('Rule name, alert type, and at least one sensor are required');
      }

      if ((input.alertType === 'sms' || input.alertType === 'both') && !input.smsPhoneNumber) {
        throw new Error('SMS phone number is required for SMS alerts');
      }

      // Create the rule
      const ruleData = {
        user_id: userId,
        rule_name: input.ruleName,
        description: input.description,
        alert_type: input.alertType,
        temp_min_enabled: input.tempMinEnabled || false,
        temp_min_value: input.tempMinValue,
        temp_max_enabled: input.tempMaxEnabled || false,
        temp_max_value: input.tempMaxValue,
        humidity_min_enabled: input.humidityMinEnabled || false,
        humidity_min_value: input.humidityMinValue,
        humidity_max_enabled: input.humidityMaxEnabled || false,
        humidity_max_value: input.humidityMaxValue,
        battery_low_enabled: input.batteryLowEnabled || false,
        battery_low_value: input.batteryLowValue,
        sms_phone_number: input.smsPhoneNumber,
        cooldown_minutes: input.cooldownMinutes || 30,
        time_window_enabled: input.timeWindowEnabled || false,
        time_window_start: input.timeWindowStart,
        time_window_end: input.timeWindowEnd,
        time_window_days: input.timeWindowDays,
        is_active: true,
      };

      const { data: rule, error: ruleError } = await supabase
        .from('device_alert_rules')
        .insert(ruleData)
        .select()
        .single();

      if (ruleError) throw ruleError;
      if (!rule) throw new Error('Rule insert returned no data');

      // Associate sensors with the rule
      if (input.sensorIds.length > 0) {
        const sensorAssociations = input.sensorIds.map(sensorId => ({
          rule_id: rule.id,
          sensor_id: sensorId,
        }));

        const { error: sensorsError } = await supabase
          .from('device_alert_rule_sensors')
          .insert(sensorAssociations);

        if (sensorsError) throw sensorsError;
      }

      // Fetch the complete rule with sensors
      const createdRule = await this.getAlertRuleById(userId, rule.id);
      if (!createdRule) {
        throw new Error('Failed to fetch created rule');
      }

      return createdRule;
    } catch (error) {
      console.error('Failed to create alert rule:', error);
      throw error;
    }
  },

  /**
   * Get a single alert rule by ID
   */
  async getAlertRuleById(userId, ruleId) {
    try {
      const rules = await this.getAlertRules(userId);
      return rules.find(r => r.id === ruleId) || null;
    } catch (error) {
      console.error('Failed to fetch alert rule:', error);
      return null;
    }
  },

  /**
   * Update an alert rule
   */
  async updateAlertRule(userId, ruleId, input) {
    try {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Build update object
      const updateData = {};
      if (input.ruleName !== undefined) updateData.rule_name = input.ruleName;
      if (input.description !== undefined) updateData.description = input.description;
      if (input.alertType !== undefined) updateData.alert_type = input.alertType;
      if (input.tempMinEnabled !== undefined) updateData.temp_min_enabled = input.tempMinEnabled;
      if (input.tempMinValue !== undefined) updateData.temp_min_value = input.tempMinValue;
      if (input.tempMaxEnabled !== undefined) updateData.temp_max_enabled = input.tempMaxEnabled;
      if (input.tempMaxValue !== undefined) updateData.temp_max_value = input.tempMaxValue;
      if (input.humidityMinEnabled !== undefined) updateData.humidity_min_enabled = input.humidityMinEnabled;
      if (input.humidityMinValue !== undefined) updateData.humidity_min_value = input.humidityMinValue;
      if (input.humidityMaxEnabled !== undefined) updateData.humidity_max_enabled = input.humidityMaxEnabled;
      if (input.humidityMaxValue !== undefined) updateData.humidity_max_value = input.humidityMaxValue;
      if (input.batteryLowEnabled !== undefined) updateData.battery_low_enabled = input.batteryLowEnabled;
      if (input.batteryLowValue !== undefined) updateData.battery_low_value = input.batteryLowValue;
      if (input.smsPhoneNumber !== undefined) updateData.sms_phone_number = input.smsPhoneNumber;
      if (input.cooldownMinutes !== undefined) updateData.cooldown_minutes = input.cooldownMinutes;
      if (input.timeWindowEnabled !== undefined) updateData.time_window_enabled = input.timeWindowEnabled;
      if (input.timeWindowStart !== undefined) updateData.time_window_start = input.timeWindowStart;
      if (input.timeWindowEnd !== undefined) updateData.time_window_end = input.timeWindowEnd;
      if (input.timeWindowDays !== undefined) updateData.time_window_days = input.timeWindowDays;

      // Update the rule
      const { error: updateError } = await supabase
        .from('device_alert_rules')
        .update(updateData)
        .eq('id', ruleId)
        .eq('user_id', userId);

      if (updateError) throw updateError;

      // Update sensor associations if provided
      if (input.sensorIds) {
        // Delete existing associations
        await supabase
          .from('device_alert_rule_sensors')
          .delete()
          .eq('rule_id', ruleId);

        // Insert new associations
        if (input.sensorIds.length > 0) {
          const sensorAssociations = input.sensorIds.map(sensorId => ({
            rule_id: ruleId,
            sensor_id: sensorId,
          }));

          const { error: sensorsError } = await supabase
            .from('device_alert_rule_sensors')
            .insert(sensorAssociations);

          if (sensorsError) throw sensorsError;
        }
      }

      // Fetch updated rule
      const updatedRule = await this.getAlertRuleById(userId, ruleId);
      if (!updatedRule) {
        throw new Error('Failed to fetch updated rule');
      }

      return updatedRule;
    } catch (error) {
      console.error('Failed to update alert rule:', error);
      throw error;
    }
  },

  /**
   * Delete an alert rule
   */
  async deleteAlertRule(userId, ruleId) {
    try {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Delete sensor associations first
      await supabase
        .from('device_alert_rule_sensors')
        .delete()
        .eq('rule_id', ruleId);

      // Delete the rule
      const { error } = await supabase
        .from('device_alert_rules')
        .delete()
        .eq('id', ruleId)
        .eq('user_id', userId);

      if (error) throw error;
    } catch (error) {
      console.error('Failed to delete alert rule:', error);
      throw error;
    }
  },

  /**
   * Toggle rule active status
   */
  async toggleRuleActive(userId, ruleId, isActive) {
    try {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        .from('device_alert_rules')
        .update({ is_active: isActive })
        .eq('id', ruleId)
        .eq('user_id', userId);

      if (error) throw error;
    } catch (error) {
      console.error('Failed to toggle rule active status:', error);
      throw error;
    }
  },
};

export default deviceAlertRuleService;

